import { normalizeToRange } from "Utility";
import { RoundTime } from "model/Match/Track/RoundTime";
import { MAX_AFP, MIN_AFP, statRoundsAfPercentile } from "../RoundStats";
import { getDriverTimesWithLastPos } from "model/Match/Track/TrackRound";
import { isFinish } from "model/Match/DriverTime";
import { TrackTimes, TrackRounds } from "model/Match/Matches";

const MIN_RATING = 0;
const MAX_RATING = 100;
const BOX_SCALING_FACTOR = 2;
const AFP_WEIGHTING = 0.25;
const RACETIME_WEIGHTING = 1 - AFP_WEIGHTING;
const RT_BEST_BOX_FRACTION = 0.2;
const RT_WORST_BOX_FRACTION = 0.2;
const RT_DNF_FRACTION = 0.05;
const RT_DNF_FACTOR = RT_DNF_FRACTION * 100;
const AFP_BEST_BOX_FRACTION = 0.15;
const AFP_WORST_BOX_FRACTION = 0.15;

export type DriverRatings = { [driverId: string]: number | undefined };

const getSortedTimes = (trackTimes: TrackTimes) => {
  const sortedTrackTimes: TrackTimes = {};
  const trackIds = Object.keys(trackTimes);
  for (let i = 0; i < trackIds.length; i++) {
    const trackId = trackIds[i];
    const roundTimes = trackTimes[trackId] ?? [];
    sortedTrackTimes[trackId] = roundTimes
      .filter(isFinish)
      .sort((a, b) => a.finishTimeMs! - b.finishTimeMs!);
  }

  return sortedTrackTimes;
};

const getDriverRounds = (trackRounds: TrackRounds, driverId: string) => {
  const driverTrackRounds: TrackRounds = {};
  const trackIds = Object.keys(trackRounds);
  for (let i = 0; i < trackIds.length; i++) {
    const trackId = trackIds[i];
    const rounds = trackRounds[trackId] ?? [];
    driverTrackRounds[trackId] = rounds.filter((r) =>
      r.times.some((t) => t.driverId === driverId)
    );
  }

  return driverTrackRounds;
};

// Returns lowest index that hasn't been tied or beaten by a different driver of `time` or faster
const getTimeIndex = (
  sortedRoundTimes: RoundTime[],
  driverId: string,
  time: number
) => {
  const matchingTimes = sortedRoundTimes.filter((m) => m.finishTimeMs === time);
  if (matchingTimes.length === 0) {
    return undefined;
  }

  const minIndex = sortedRoundTimes.indexOf(matchingTimes[0]);
  // If some of the matching times aren't the same driver, return lowest tied index.
  if (matchingTimes.some((d) => d.driverId !== driverId)) {
    return minIndex;
  }

  // All tied times are driver, look for their best consecutive time not beaten or tied by someone else
  let currentIndex = minIndex;
  while (currentIndex > 0) {
    const nextBest = sortedRoundTimes[currentIndex - 1];
    // Same driver, continue down
    if (nextBest.driverId === driverId) {
      currentIndex--;
    }
    // Different driver, find the time that is next best from driver we're getting index for
    else {
      // Go back till we find the next worst non-tied time.
      // This is the drivers consecutive best that hasn't been tied or beaten by someone else
      while (
        nextBest.finishTimeMs === sortedRoundTimes[currentIndex].finishTimeMs
      ) {
        currentIndex++;
      }

      return currentIndex;
    }
  }

  // No faster or tied time by someone else, return 0 as they are best.
  return 0;
};

export const getDriverRatings = (
  driverIds: string[],
  allTimes: TrackTimes,
  evaluatedRounds: TrackRounds
) => {
  const sortedAllTimes = getSortedTimes(allTimes);
  const driverRatings: DriverRatings = {};
  for (let i = 0; i < driverIds.length; i++) {
    const driverId = driverIds[i];
    const driverRounds = getDriverRounds(evaluatedRounds, driverId);
    driverRatings[driverId] = calculateDriverRating(
      driverId,
      sortedAllTimes,
      driverRounds
    );
  }

  return driverRatings;
};

export const calculateDriverRating = (
  driverId: string,
  sortedAllTrackTimes: TrackTimes,
  driverTrackRounds: TrackRounds
) => {
  const trackRatings: number[] = [];
  const trackIds = Object.keys(sortedAllTrackTimes);
  for (let i = 0; i < trackIds.length; i++) {
    const trackId = trackIds[i];
    const sortedTrackTimes = sortedAllTrackTimes[trackId];
    const driverRounds = driverTrackRounds[trackId];
    if (
      !sortedTrackTimes ||
      sortedTrackTimes.length === 0 ||
      !driverRounds ||
      driverRounds.length === 0
    ) {
      continue;
    }

    const driverTimesWithLastPos = getDriverTimesWithLastPos(
      driverRounds,
      driverId
    );

    const trackRating = calculateTrackRating(
      driverId,
      sortedTrackTimes,
      driverTimesWithLastPos
    );

    if (trackRating !== undefined) {
      trackRatings.push(trackRating);
    }
  }

  const numTrackRatings = trackRatings.length;
  if (numTrackRatings === 0) {
    return undefined;
  }

  const total = trackRatings.reduce((a, b) => a + b, 0);
  return total / numTrackRatings;
};

const calculateTrackRating = (
  driverId: string,
  sortedTrackTimes: RoundTime[],
  driverTimesWithLastPos: { time: RoundTime; lastPos: number }[]
) => {
  const driverFinishTimes = driverTimesWithLastPos
    .map((t) => t.time.finishTimeMs)
    .filter((t) => !!t) as number[];

  const timeRating = calculateTimeRating(
    driverId,
    sortedTrackTimes,
    driverFinishTimes
  );

  if (timeRating === undefined) {
    return undefined;
  }

  const afpRating = calculateAfpRating(driverTimesWithLastPos);
  if (afpRating === undefined) {
    return undefined;
  }

  // Return weighted average of AFP and Time ratings to get Track rating
  return (
    (AFP_WEIGHTING * afpRating + timeRating * RACETIME_WEIGHTING) /
    (AFP_WEIGHTING + RACETIME_WEIGHTING)
  );
};

const calculateTimeRating = (
  driverId: string,
  sortedTimes: RoundTime[],
  driverTimes: number[]
) => {
  if (driverTimes.length === 0) {
    return undefined;
  }

  const numberOfTimes = sortedTimes.length;
  const maxTimeIndex = numberOfTimes - 1;
  // The largest index boxed at fastest end
  const maxFastBoxIndex = Math.max(
    0,
    Math.round(numberOfTimes * RT_BEST_BOX_FRACTION) - 1
  );

  // The lowest index boxed at slowest end
  const minSlowBoxIndex = Math.min(
    maxTimeIndex,
    Math.round(numberOfTimes * (1 - RT_WORST_BOX_FRACTION) - 1)
  );

  const unboxedUpperBound = MAX_RATING - RT_DNF_FACTOR - BOX_SCALING_FACTOR;
  const boxedUpperBound = MAX_RATING - RT_DNF_FACTOR;

  // Driver time weights calculated as lower number better
  const weights: number[] = [];
  for (let i = 0; i < driverTimes.length; i++) {
    const weight = getWeightedTimeIndex(
      driverId,
      sortedTimes,
      driverTimes[i],
      maxTimeIndex,
      minSlowBoxIndex,
      maxFastBoxIndex,
      boxedUpperBound,
      unboxedUpperBound
    );

    if (weight !== undefined) {
      weights.push(weight);
    }
  }

  if (weights.length === 0) {
    return undefined;
  }

  const total = weights.reduce((a, b) => a + b, 0);
  const average = total === 0 ? 0 : total / weights.length;

  // Inverse so that larger is better (weights individually calc'ed as lower better)
  return MAX_RATING - average;
};

const calculateAfpRating = (
  driverTimesWithLastPos: { time: RoundTime; lastPos: number }[]
) => {
  const minBestBoxAfp = MAX_AFP - AFP_BEST_BOX_FRACTION;
  const maxWorstBoxAfp = MIN_AFP + AFP_WORST_BOX_FRACTION;
  const driverAfp = statRoundsAfPercentile(driverTimesWithLastPos);
  if (driverAfp === undefined) {
    return undefined;
  }

  // If AFP in best box, get a normalised value between in the best box range
  if (driverAfp >= minBestBoxAfp) {
    return normalizeToRange(
      driverAfp,
      MIN_AFP,
      MAX_AFP,
      MAX_RATING - BOX_SCALING_FACTOR,
      MAX_RATING
    );
  }

  // If AFP in worst box, get a normalised value between in the worst box range
  if (driverAfp <= maxWorstBoxAfp) {
    return normalizeToRange(
      driverAfp,
      MIN_AFP,
      MAX_AFP,
      MIN_RATING,
      MIN_RATING + BOX_SCALING_FACTOR
    );
  }

  // AFP not boxed, get a normalised value in the standard unboxed range
  return normalizeToRange(
    driverAfp,
    maxWorstBoxAfp,
    minBestBoxAfp,
    MIN_RATING + BOX_SCALING_FACTOR,
    MAX_RATING - BOX_SCALING_FACTOR
  );
};

const getWeightedTimeIndex = (
  driverId: string,
  sortedTimes: RoundTime[],
  time: number,
  maxTimeIndex: number,
  minSlowBoxIndex: number,
  maxFastBoxIndex: number,
  boxedUpperBound: number,
  unboxedUpperBound: number
) => {
  // if DNF award worst weight
  if (time < 0) {
    return MAX_RATING;
  }

  const timeIndex = getTimeIndex(sortedTimes, driverId, time);
  if (timeIndex === undefined) {
    return undefined;
  }

  // If index in fastest box, get a normalised weight in the fastest box range
  if (timeIndex <= maxFastBoxIndex) {
    return normalizeToRange(
      timeIndex,
      0,
      maxFastBoxIndex,
      MIN_RATING,
      MIN_RATING + BOX_SCALING_FACTOR
    );
  }

  // If index in slowest box, get a normalised weight in the slowest box range
  if (timeIndex >= minSlowBoxIndex) {
    return normalizeToRange(
      timeIndex,
      minSlowBoxIndex,
      maxTimeIndex,
      unboxedUpperBound,
      boxedUpperBound
    );
  }

  // Index not boxed, get a normalised weight in the standard unboxed range
  return normalizeToRange(
    timeIndex,
    maxFastBoxIndex,
    minSlowBoxIndex,
    MIN_RATING + BOX_SCALING_FACTOR,
    unboxedUpperBound
  );
};
